import { useCallback, useEffect } from 'react';

import { AdvancedSearchData } from '@/components/search/types';
import { useLoading } from '@/contextProviders/loadingProvider';
import { useFetchWithAuth } from '@/queries/useFetchWithAuth';
import { postRequestInit } from '@/queries/utils';
import { fetcher } from '@/queries/werklijnen/fetcher';
import { useQuery } from '@tanstack/react-query';

export function useAdvancedSearchApiCombineResults(data: AdvancedSearchData[]) {
  const { fetchWithAuth } = useFetchWithAuth();
  const { setLoading } = useLoading();
  const { refetch, isLoading, ...queryResult } = useQuery({
    queryKey: ['advancedSearchDataVoorToevoegen', data],
    queryFn: async () => {
      return await Promise.all(data.map((value) => fetcher(value, '/werklijnen/zoek', fetchWithAuth)));
    },
    enabled: !!data && data.length >= 1,
  });
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  return { refetch, ...queryResult };
}

/**
 * React Query-based versie van deze API t.b.v. interactief gebruik (incl. loading spinner)
 */
export function useAdvancedSearchApi(data: AdvancedSearchData | null | undefined) {
  const { fetchWithAuth } = useFetchWithAuth();
  const { setLoading } = useLoading();

  const { isLoading, ...queryResult } = useQuery({
    queryKey: ['advancedSearchData', data],
    queryFn: () => fetcher(data, '/werklijnen/zoek', fetchWithAuth),
    // only enable the fetch when there are more than 1 criterion ( so not with only a date, but a date and a location and/or trainNumber)
    enabled: !!data && data.criteria.length >= 1,
  });

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  return queryResult;
}

/**
 * Simple wrapper for this API with the same API as `fetch` for non-interactive gebruik e.g. SSE refreshes.
 */
export function useFetchAdvancedSearchApi() {
  const { fetchWithAuth } = useFetchWithAuth();

  const fetchAdvancedSearchApi = useCallback(
    async (data: AdvancedSearchData) => {
      return await fetchWithAuth('/werklijnen/zoek', postRequestInit(data));
    },
    [fetchWithAuth],
  );

  return { fetchAdvancedSearchApi };
}
